class ProductVariantAnalytics {
  logging = false;

  localStorageKeys = {
    'view_item': 'viewItemAnalytics',
    'add_to_cart': 'addToCartAnalytics',
    'remove_from_cart': 'removeFromCartAnalytics',
    'add_to_cart_tab': 'addToCartTabAnalytics',
    'view_item_list': 'viewItemListAnalytics',
  };

  /**
   * If $viewItem exists (i.e. the user is viewing the PDP),
   * immediately fires the onViewItem handler with its analytics.
   * Binds an event handler for the custom onAddToCart event.
   */
  constructor() {
    // PDP product assignments
    const $viewItem = $('[data-on-view-item][data-analytics]');

    if ($viewItem.length) {
      this.onViewItem(this.getAnalytics($viewItem));
    }

    document.addEventListener('onAddToCart', this.onAddToCart.bind(this));
    document.addEventListener('onChangeQuantityInCart', this.onChangeQuantityInCart.bind(this));

    // Related Products assignments
    this.$document = $(document);
    this.$relatedProductsSection = $('.section--related-products');
    this.$relatedProductsInner = this.$relatedProductsSection.find(".related-products__tabs-wrapper");
    this.$activeTab = this.getRelatedProductsActiveTab();
    this.activeProductCards = this.getActiveRelatedProductCards();
    this.currentCardsArr = [];
    this.relatedProductsSeen = false;

    if (this.$relatedProductsInner.length) {
      this.onRelatedProductsSeen();
      this.onRelatedProductsNewTabClick();
      this.onRelatedProductClick();
      this.onRelatedProductsSliderButtonClick();
    }
  }

  /**
   * Parses and returns JSON from an $element's [data-analytics] attribute.
   *
   * @param {jQuery} $element with [data-analytics].
   *
   * @returns {object|null} Analytics object or null.
   */
  getAnalytics($element) {
    return JSON.parse($element.data('analytics')) ?? null;
  }

  /**
   * Returns a label for analytics events in the following format: `tab name > product-handle`,
   * provided that the passed $element is within the Related Products section.
   *
   * @param {jQuery} $element within the Related Product section.
   *
   * @returns {string|null} formatted name -- `tab name > product-handle` -- of tab for analytics..
   */
  getRelatedProductsTabAnalyticsName($element) {
    if (!this.$relatedProductsInner || !$element || !$element.closest('.section--related-products').length) return null;
    let name = null;
    const $activeTabButton = this.$relatedProductsInner.find('.nav-link.active');
    const getSlug = url => new URL(url).pathname.match(/[^/]+/g)
    const tabTitle = $activeTabButton ? $activeTabButton.text().toLowerCase() : null;
    const slugs = getSlug(window.location.href);
    const slug = slugs[slugs.length - 1]
    name = tabTitle && slug ? `${tabTitle} > ${slug}` : null;
    return name;
  }

  /**
   * Finds an $element's analytics from its checked descendent input.
   * i.e., if you need to know the current state of a product's analytics
   * when an ancestor is interacted with – i.e., when its parent <a> is
   * clicked – this will search within that ancestor $element to find
   * the checked variant input and the analytics that correspond to
   * that input.
   *
   * @param {jQuery} $element with [data-analytics].
   *
   * @returns {object|null} Analytics object or null.
   */
  getAnalyticsFromCheckedInput($element = null) {
    if (!$element) return null;
    let analytics = null;
    const $form = $element.find('.product-variant-form');
    const $variants = $form.find('.variants');
    const $checkedVariantInput = $variants.find("input:checked");
    if ($checkedVariantInput && $checkedVariantInput.length === 1) {
      analytics = this.getAnalytics($checkedVariantInput)
    }
    return analytics;
  }

  /**
   * Gets the Related Product tab which currently has active classes.
   *
   * @returns {jQuery|null} jQuery object or null.
   */
  getRelatedProductsActiveTab() {
    if (!this.$relatedProductsSection.length) return null;
    return this.$relatedProductsSection.find('.tab-pane.show.active');
  }

  /**
   * Gets the Related Product cards that are currently active, from within the tab that is currently active.
   *
   * @returns {jQuery|null} jQuery object or null.
   */
  getActiveRelatedProductCards() {
    if (!this.$activeTab?.length) return null;
    return this.$activeTab.find('.slick-active .product__card');
  }

  /**
   * onViewItem "event" handler. If $viewItem exists in the class
   * constructor, (optionally logs and) stores its analytics.
   *
   * @param {object|null} analytics Analytics object or null.
   */
  onViewItem(analytics) {
    if (analytics) {
      const addToCartTabAnalyticsStorageKey = 'addToCartTabAnalytics';
      const relatedProductsTabAnalytics = localStorage.getItem(addToCartTabAnalyticsStorageKey);
      if (relatedProductsTabAnalytics) {
        analytics.item_list_name = JSON.parse(relatedProductsTabAnalytics);
        localStorage.removeItem(addToCartTabAnalyticsStorageKey);
      }

      this.logging && console.log('ProductVariantAnalytics.onViewItem:', analytics);
      this.storeAnalyticsForEvent('view_item', analytics);
    }
  }

  /**
   * onAddToCart event handler. Receives a custom event with the
   * added-to-cart variant's ID, which is then used to find its
   * corresponding [data-on-add-to-cart] element on the page and
   * (optionally log and) store its analytics.
   *
   * This event can be dispatched like so:
   *
   * document.dispatchEvent(new CustomEvent('onAddToCart', {
   *   detail: { variantId },
   * }));
   *
   * Refer to add-to-cart-form.js for a working example.
   *
   * @param {Event} Event handler with { detail: { variantId } }.
   */
  onAddToCart({ detail: { variantId }}) {
    const $variant = $(`[data-on-add-to-cart="${variantId}"][data-analytics]`);

    if ($variant.length) {
      const analytics = this.getAnalytics($variant);

      if (analytics) {
        const tabName = this.getRelatedProductsTabAnalyticsName($variant);
        if (tabName) {
          analytics.item_list_name = tabName;
        }
        this.logging && console.log('ProductVariantAnalytics.onAddToCart:', analytics);
        this.storeAnalyticsForEvent('add_to_cart', analytics);
      }
    }
  }

  /**
   * onChangeQuantityInCart event handler. Receives a custom event with
   * the variant ID of the changed product, which is then used to find
   * its corresponding [data-on-change-quantity-in-cart] element on the
   * page and (optionally log and) store its analytics.
   *
   * This event can be dispatched like so:
   *
   * document.dispatchEvent(new CustomEvent('onChangeQuantityInCart', {
   *   detail: {
   *    variantId,   // Variant ID.
   *    changeType,  // One of 'increase', 'decrease', 'remove'.
   *    changeAmount // The amount the quantity is changed by.
   *  },
   * }));
   *
   * Refer to cart.script.liquid for a working example.
   *
   * @param {Event} Event handler.
   */
  onChangeQuantityInCart({ detail: { variantId, changeType, changeAmount }}) {
    const $variant = $(`[data-on-change-quantity-in-cart="${variantId}"][data-analytics]`);

    if ($variant.length) {
      let analytics = this.getAnalytics($variant);

      if (analytics) {
        analytics = Object.assign(
          {
            'cart_event': changeType,
            'quantity': changeAmount,
          },
          analytics
        );

        this.logging && console.log('ProductVariantAnalytics.onChangeQuantityInCart:', analytics);

        const event = changeType === 'increase' ? 'add_to_cart' : 'remove_from_cart';

        this.storeAnalyticsForEvent(event, analytics);
      }
    }
  }

  /**
   * When a Related Products item is clicked, stores the containing tab name to be included in 'product_viewed' analytics.
   *
   */
  onRelatedProductClick() {
    this.$document.on('click', '.related-products__tabs-wrapper .tab-pane.show.active .product__card a', (e) => {
      e.preventDefault();
      const $a = $(e.currentTarget);
      const tabName = this.getRelatedProductsTabAnalyticsName($a);
      this.storeAnalyticsForEvent('add_to_cart_tab', tabName);
      window.location.href = $a.attr('href');
    });
  }

  /**
   * When the Related Products section's carousel is paginated, runs multiple events:
   *
   * 1) Gets an array of the new carousel cards.
   * 2) The new card is determined based on the button that's clicked.
   *    NOTE: A filter on the array, diffing it against the previous 'state', might be preferable but was unreliable in testing.
   * 3) The 'new card' – that which wasn't in the previous snapshot of 'current cards' – has its analytics processed.
   * 4) The currentCardsArr is reassigned to the ‘incoming’ cards (as the new source of truth).
   *
   */
  onRelatedProductsSliderButtonClick() {
    // For reasons not yet cler, making a elegant target (.slick-arrow, e.g.)
    // wasn't firing when, e.g., the prevButton was returning to its disabled state.
    // Hence this loose target that is then checked in the condition.
    this.$document.on('click', '.related-products__tabs-wrapper .tab-pane.show.active', (e) => {
      const $target = $(e.target);
      // Some of the data does not seem to refresh instantaneously, hence the timeout.
      setTimeout(() => {
        let $newCard = null;

        const incomingCardsArr = $.makeArray(this.getRelatedProductsActiveTab().find('.slick-slide.slick-active article.product__card'));

        if ($target.hasClass('slick-next')) {
          $newCard = $(incomingCardsArr[incomingCardsArr.length - 1])
        } else if ($target.hasClass('slick-prev')) {
          $newCard = $(incomingCardsArr[0]);
        }

        if ($newCard) {
          const itemAnalytics = this.getAnalyticsFromCheckedInput($newCard);
          if (itemAnalytics) {
            const analytics = {
              event: 'view_item_list',
              item_list_name: this.getRelatedProductsTabAnalyticsName($target),
              items: [
                itemAnalytics,
              ],
            };

            this.logging && console.log('RelatedProductsAnalytics.onRelatedProductsSliderButtonClick:', analytics);
            this.storeAnalyticsForEvent('view_item_list', analytics);
            Shopify.analytics.publish('view_item_list');
          }
        }

        this.currentCardsArr = incomingCardsArr;
      }, 250)
    });
  }

  /**
   * When a new Related Products tab is clicked, runs multiple events:
   *
   * 1) Checks if the tab is changing. (If the already active tab link is clicked,
   *    it does nothing.)
   * 2) If it is changing, reassigns the active tab and the active product cards,
   *    and processes the analytics for the new cards.
   *
   */
  onRelatedProductsNewTabClick() {
    this.$document.on('click', '.related-products__tabs-wrapper .nav-tabs .nav-link', (e) => {
      const incomingTabId = $(e.target).data('bs-target');
      const activeTabId = `#${this.$activeTab.attr('id')}`

      if (incomingTabId !== activeTabId) {
        this.$activeTab = $(incomingTabId);
        this.activeProductCards = this.getActiveRelatedProductCards();
        this.onRelatedProductsTabSeen();
      }
    });
  }

  /**
   * When a Related Products tab is seen, whether through the initial scroll,
   * or from another tab being activated, this stores the requested analytics
   * data to local storage and publishes a Shopify analytics event to consume
   * the data.
   *
   */
  onRelatedProductsTabSeen() {
    const analytics = {};
    const $cards = this.getActiveRelatedProductCards();
    analytics.item_list_name = this.getRelatedProductsTabAnalyticsName($cards);
    analytics.items = $cards.length ? new Array() : null;
    $cards.each((i, el) => {
      const $el = $(el)
      const itemAnalytics = this.getAnalyticsFromCheckedInput($el);
      if (itemAnalytics) {
        analytics.items.push(itemAnalytics);
      }
    })
    this.logging && console.log('RelatedProductsAnalytics.onRelatedProductsTabSeen', analytics);
    this.storeAnalyticsForEvent('view_item_list', analytics);
    Shopify.analytics.publish('view_item_list');
  }

  /**
   * When the Related Product section's content is seen/scrolled into view, runs multiple events:
   *
   * 1) Sends through the analytics of the initial batch of cards.
   * 2) Reassigns the 'currentCardsArr' to the cards available on, more or less, load.
   *   2.1) That said, the initial cards may not be present on load/scroll-into-view exactly
   *        (if still loading from API call), so some intervals and timeouts are used to handle that case.
   *
   */
  onRelatedProductsSeen() {
    if (!this.$relatedProductsInner) return;
    let currentCardsInterval;
    const relatedProductsElement = this.$relatedProductsInner.get(0);

    const options = {
      rootMargin: '-1px 0px 0px 0px', // Slight negative margin to trigger near top
      threshold: [0, 1], // Trigger when entering and leaving the viewport
    };

    const observer = new IntersectionObserver((entry) => {
      // this.relatedProductsSeen is used to only post these initial card view analytics once.
      if(entry[0].isIntersecting) {
        // Destroy the observer once it's seen for the first time.
        observer.unobserve(relatedProductsElement);

        // Cards may not have loaded by the time the intersection observer intersects,
        // especially in the API-driven first tab, so we'll set an interval till they have.
        currentCardsInterval = setInterval(() => {
          if (this.getActiveRelatedProductCards().length) {
            // Cards also may not be in their 'final' state immediately – there seems to be a reload
            // that occurs, or time it takes for Slick to fire, which lead to buggy setting of and
            // comparisons against the currentCardsArr – so the timeout addresses this.
            clearInterval(currentCardsInterval);

            setTimeout(() => {
              this.currentCardsArr = $.makeArray(this.getActiveRelatedProductCards());
              this.onRelatedProductsTabSeen();
            }, 250)
          }
        }, 250);
      }
    });
    observer.observe(relatedProductsElement, options);
  }

  /**
   * Writes an event's analytics to a corresponding localStorage
   * key. Subsequent add_to_cart events' analytics (e.g. when adding
   * multiple variants to a cart via the PDP) are preserved in an
   * array so that the resulting product_added_to_cart custom pixel
   * events receive each variant's analytics (not just the last one).
   *
   * @param {string} event     The event key from this.localStorageKeys.
   * @param {object} analytics The event's analytics object.
   */
  storeAnalyticsForEvent(event, analytics) {
    const key = this.localStorageKeys[event];
    let value;

    if ('add_to_cart' === event) {
      value = JSON.parse(localStorage.getItem(key)) ?? [];

      value.push(analytics);
    } else {
      value = analytics;
    }

    localStorage.setItem(key, JSON.stringify(value));
  }
}

(($) => {
  new ProductVariantAnalytics();
})(jQuery);
